import { Button } from '@mui/material';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Link, Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';

import { AuthenticatedArea, PrivateRoute, FeatureRoute } from 'components/AuthenticatedArea';
import PageFallback from 'components/PageFallback';
import PageWip from 'components/PageWip';
import LandingLayout from 'layout/LandingLayout';

import { UserRolesTypes } from 'common/user-role-types';
import { isUserFinancier } from 'utils/auth';
import AdminRoutes from 'views/admin/AdminRoutes';
import FinancierRoutes from 'views/admin/FinancierRoutes';
import ManualWithdraw from 'views/dashboard/private/ManualWithdraw';
import BankingRoutes from 'views/dashboard/private/Banking/BankingRoutes';

/** Views */
const Onboarding = lazy(() => import('views/landing/Onboarding'));
const AuthLogin = lazy(() => import('views/dashboard/public/Login'));
const AuthLogout = lazy(() => import('views/dashboard/public/Logout'));
const ForgotPassword = lazy(() => import('views/dashboard/public/ForgotPassword'));
const WelcomePage = lazy(() => import('views/dashboard/private/WelcomePage'));
const UserRegistration = lazy(() => import('views/dashboard/private/UserRegistration'));
const UploadAndContract = lazy(() => import('views/dashboard/private/UploadAndContract'));
const Guarantee = lazy(() => import('views/dashboard/private/Garantia'));
const ContractConfirmation = lazy(() => import('views/dashboard/private/ContractConfirmation'));
const GuaranteeContractConfirmation = lazy(() => import('views/dashboard/private/ContractConfirmation/GuaranteeContractConfirmation'));
// const Contracts = lazy(() => import('views/dashboard/private/Contracts'));
const ContractDetail = lazy(() => import('views/dashboard/private/ContractDetail'));
const CompleteRegistration = lazy(() => import('views/dashboard/public/CompleteRegistration'));
const Reports = lazy(() => import('views/dashboard/private/ReportsPanel'));
const RevenueReport = lazy(() => import('views/dashboard/private/RevenueReport'));
const GuaranteePanel = lazy(() => import('views/dashboard/private/GuaranteePanel'));
const FileManagementPage = lazy(() => import('views/dashboard/private/FileDashboard/FileManagement'));
const FileUploadPage = lazy(() => import('views/dashboard/private/FileDashboard/FileUpload'));
const PublicUploadFile = lazy(() => import('views/dashboard/public/PublicUploadFile'));
const FileDashboard = lazy(() => import('views/dashboard/private/FileDashboard'));
const BankingExternalApprovalPage = lazy(() => import('views/dashboard/private/Banking/ExternalApproval'));

const Routes = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <ReactRoutes>
          {/* Public Routes */}
          <Route
            path="/cadastro"
            element={
              <LandingLayout>
                <Onboarding />
              </LandingLayout>
            }
          />
          <Route path="/public-file-upload" element={<PublicUploadFile />} />
          <Route path="/banking/external-approval/:transactionId" element={<BankingExternalApprovalPage />} />
          <Route path="/login" element={<AuthLogin />} />
          <Route path="/logout" element={<AuthLogout />} />
          <Route path="/recuperar-senha" element={<ForgotPassword />} />
          <Route path="/finalizar-cadastro" element={<CompleteRegistration />} />
          <Route
            path="/soon"
            element={
              <PageWip>
                <Link to="/" style={{ textDecoration: 'none', marginTop: 10 }}>
                  <Button variant="contained" color="secondary">
                    Ir para início
                  </Button>
                </Link>
              </PageWip>
            }
          />

          {/* Private Routes */}
          <Route element={<AuthenticatedArea />}>
            <Route path="/bem-vindo" element={<WelcomePage />} />
            <Route path="/primeiro-acesso/usuarios/novo" element={<UserRegistration />} />
            <Route element={<FeatureRoute featureKey="anticipation" />}>
              <Route path="/contratar" element={<UploadAndContract />} />
              <Route path="/confirmacao" element={<ContractConfirmation />} />
            </Route>
            <Route element={<FeatureRoute featureKey="guarantee" />}>
              <Route path="/garantia" element={<Guarantee />} />
              <Route path="/confirmacao/garantia" element={<GuaranteeContractConfirmation />} />
            </Route>
            <Route path="/consulta-contratos" element={<Navigate to="/relatorios" replace />} />
            <Route path="/retirada-manual" element={<ManualWithdraw />} />
            <Route path="/contratos/:id" element={<ContractDetail />} />

            <Route element={<PrivateRoute permittedRoles={[UserRolesTypes.ADMIN]} />}>
              <Route path="/admin/*" element={<AdminRoutes />} />
            </Route>

            <Route element={<PrivateRoute permittedRoles={[UserRolesTypes.FINANCIER]} />}>
              <Route path="/financier/*" element={<FinancierRoutes />} />
            </Route>

            <Route element={<FeatureRoute featureKey="reports" />}>
              <Route path="/relatorios" element={<Reports />} />
              <Route
                path="/relatorios/repasses"
                element={isUserFinancier() ? <Navigate to="/relatorios/painel-garantia" replace /> : <RevenueReport />}
              />
              <Route path="/relatorios/painel-garantia" element={<GuaranteePanel />} />
              <Route path="/relatorios/ajustes" element={<Navigate to="/relatorios" replace />} />
            </Route>
            <Route
              element={
                <PrivateRoute permittedRoles={[UserRolesTypes.ADMIN, UserRolesTypes.CUSTOMER_SUPERVISOR, UserRolesTypes.CUSTOMER_OWNER]} />
              }
            >
              <Route path="/banking/*" element={<BankingRoutes />} />
            </Route>

            <Route path="/documentacao" element={<FileDashboard />}>
              <Route index element={<Navigate to="gerenciamento" replace />} />
              <Route path="gerenciamento" element={<FileManagementPage />} />
              <Route path="upload" element={<FileUploadPage />} />
            </Route>
          </Route>

          {/* Default Route match */}
          <Route path="*" element={<Navigate to="/login" />} />
        </ReactRoutes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Routes;
